





























import { Component, Watch, Prop } from "vue-property-decorator";
import {
  BaseStore,
  CanopyStore,
  FrameStore,
  LayoutStore,
  ShapeStore,
  SurchargeStore,
} from "@/mixins/store";
import { mixins } from "vue-class-component";
import ProductService from "@/services/product_service";
import { Auth } from "@/mixins/auth";
import { PriceBreakdown } from "@/models/pricing/price";
import { AuthError, GenericError } from "@/services/error_service";
import { Preset } from "@/models/configurator/configurator_options";
import PricingBreakdown from "@/components/order_form/PricingBreakdown.vue";
import { ConfigCreator } from "@/mixins/configurator";
import { EventBus, PRICING_SUSPEND, PRICING_RESUME } from "@/event-bus";

@Component({
  components: {
    PricingBreakdown,
  },
})
export default class Pricing extends mixins(
  ShapeStore,
  CanopyStore,
  BaseStore,
  LayoutStore,
  FrameStore,
  ConfigCreator,
  Auth,
  SurchargeStore
) {
  @Prop({ default: 1 }) qty!: number;
  protected productService = new ProductService();
  protected showPriceModal = false;
  protected breakdown: PriceBreakdown | null = null;
  protected Preset = Preset;
  private priceRequestId = 0;
  private priceUpdateQueued = false;
  private pricingSuspended = false;

  created(): void {
    EventBus.$on(PRICING_SUSPEND, this.suspendPricing);
    EventBus.$on(PRICING_RESUME, this.resumePricing);
  }

  beforeDestroy(): void {
    EventBus.$off(PRICING_SUSPEND, this.suspendPricing);
    EventBus.$off(PRICING_RESUME, this.resumePricing);
  }

  /**
   * Hold off on price/SKU requests while a caller applies a multi-step config
   * change. Without this, a watcher firing mid-sequence sends a request built
   * from a partially-applied config (e.g. the stock reset's vent fabric had not
   * landed yet), and that stale price is what the user sees.
   */
  private suspendPricing(): void {
    this.pricingSuspended = true;
  }

  private async resumePricing(): Promise<void> {
    if (!this.pricingSuspended) return;
    this.pricingSuspended = false;
    await this.updatePriceAndSKU();
  }

  protected toggleModal(): void {
    this.showPriceModal = true;
  }

  @Watch("preset", { deep: true })
  protected async updatePreset(): Promise<void> {
    this.updatePriceAndSKU();
  }

  @Watch("umbrellaModel", { immediate: true, deep: true })
  protected updateUmbrella(): void {
    this.updatePriceAndSKU();
  }

  @Watch("finish", { deep: true })
  protected updateFrameFinish(): void {
    this.updatePriceAndSKU();
  }

  @Watch("finial", { deep: true })
  protected updateFinial(): void {
    this.updatePriceAndSKU();
  }

  @Watch("finialFinish", { deep: true })
  protected updateFinialFinish(): void {
    this.updatePriceAndSKU();
  }

  @Watch("bottomPole", { deep: true })
  protected updateBottomPole(): void {
    this.updatePriceAndSKU();
  }

  @Watch("spigot", { deep: true })
  protected updateSpigot(): void {
    this.updatePriceAndSKU();
  }

  @Watch("altaPagoda")
  protected updateAltaPagoda(): void {
    this.updatePriceAndSKU();
  }

  @Watch("baseFinish", { deep: true })
  protected updateBaseFinish(): void {
    this.updatePriceAndSKU();
  }

  @Watch("baseModel", { immediate: true, deep: true })
  protected updateBase(): void {
    this.updatePriceAndSKU();
  }

  @Watch("baseOptions", { immediate: true, deep: true })
  protected updateBaseOptions(): void {
    this.updatePriceAndSKU();
  }

  @Watch("allCanopyData", { deep: true })
  protected updateCanopy(): void {
    this.updatePriceAndSKU();
  }

  @Watch("valanceFabric", { deep: true })
  protected updateValance(): void {
    this.updatePriceAndSKU();
  }

  async updatePriceAndSKU(): Promise<void> {
    if (this.pricingSuspended) return;
    if (this.priceUpdateQueued) return;
    this.priceUpdateQueued = true;
    await this.$nextTick();
    this.priceUpdateQueued = false;
    await Promise.all([this.updatePrice(), this.updateSKU()]);
  }

  async updatePrice(): Promise<void> {
    const requestId = ++this.priceRequestId;
    try {
      const result = await this.productService.getTotal();
      if (requestId !== this.priceRequestId) return;
      // the API answers 200 with no data when it cannot calculate a price
      this.breakdown = result?.breakdown ?? null;
      // FUMBSD-761: keeps the disclaimer up if GET /prices/surcharge failed
      if (this.breakdown) this.seedSurcharge(this.breakdown.surcharge);
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  async updateSKU(): Promise<void> {
    const data = await this.transformDataToConfigFile();
    const sku = await this.productService.createFinishedUmbrellaSKU(data);
    this.$emit("updateSKU", sku);
  }

  protected close(): void {
    this.showPriceModal = false;
  }

  /**
   * FUMBSD-761: every amount comes from the API's breakdown, so the surcharge is
   * its own line. All lines are multiplied by qty and the customer multiplier.
   */
  private get scale(): number {
    return this.msrpMultiplier * this.qty;
  }

  private get umbrellaSubtotal(): number {
    return this.breakdown?.umbrella?.subtotal ?? 0;
  }

  private get baseSubtotal(): number {
    return this.breakdown?.base?.subtotal ?? 0;
  }

  private roundCents(value: number): number {
    return Math.round(value * 100) / 100;
  }

  /** Surcharge-inclusive total, as shown in the footer and the modal */
  private get totalAmount(): number {
    const rate = this.breakdown?.surcharge.rate ?? 0;
    return this.roundCents(
      (this.umbrellaSubtotal + this.baseSubtotal) * (1 + rate) * this.scale
    );
  }

  private get umbrellaAmount(): number {
    return this.roundCents(this.umbrellaSubtotal * this.scale);
  }

  private get baseAmount(): number {
    return this.roundCents(this.baseSubtotal * this.scale);
  }

  get price(): string {
    return this.totalAmount.toFixed(2);
  }

  get umbrellaPrice(): string {
    return this.umbrellaAmount.toFixed(2);
  }

  get basePrice(): string {
    return this.baseAmount.toFixed(2);
  }

  /** The remainder, so umbrella + base + surcharge always equals the total to the cent */
  get surchargePrice(): string {
    return this.roundCents(
      this.totalAmount - this.umbrellaAmount - this.baseAmount
    ).toFixed(2);
  }

  /** Empty when there is no surcharge, which hides the line */
  get surchargeLabel(): string {
    return this.breakdown?.surcharge.label ?? "";
  }
}
